import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {repeat} from 'lit/directives/repeat.js';
import {classMap} from 'lit/directives/class-map.js';
import {connect} from 'pwa-helpers';
import store from '../../../store';
import {cssProperties} from '../../../css/cssProperties';
import {getProductData} from '../../../util/getProductData';
import {getValueOfFeature, isSatisfied, pimText, pimTextDebug} from '../../../util/values';

@customElement('visual-lightprofile-summary-colors')
export class VisualLightprofileSummaryColors extends connect(store)(LitElement) {

	static styles = [
		css`
		.lampsColors {
			padding: 100px 30px;
		}

		.description {
			color: var(--color-grey4);
			font: var(--font-summary-paragraph);
			line-height: 22px;
			max-width: 70%;
			text-align: left;
		}

		.description h5 {
			color: var(--color-white);
			letter-spacing: 0.2;
		}

		.colorChoice {
		    display: grid;
    		grid-auto-flow: dense;
   			grid-template-columns: 3fr 9fr;
    		gap: 0px;
   			padding: 0px 16px 200px 0px;
		 }

		.colorList {
			column-gap: 35px;
			display: grid;
			grid-auto-flow: row dense;
			grid-template-columns: repeat(2, 6fr);
			grid-template-rows: 6;
			margin-left: 20px;
		}

		.colorContainer {
		  	border-bottom: 1px solid #606060;
			border-radius: 2px;
		  	height: 39px;
		}

		.colorName {
			color: var(--color-grey4);
			font-family: var(--font-family-monospace);
			font-size: var(--font-size-widget-small-mono);
			margin-bottom: 0px;
		 	max-width: 100%;
		 	line-height: 19px;
		 	padding-left: 10px;
			text-transform: capitalize;
		}

		.colorDot {
			height: 18px;
			width: 18px;
			background-color: #fff;
			border-radius: 50%;
			float: right;
			margin-right: 10px;
		}

		.selected {
			background-color:  var(--color-grey3);
			  .colorName {
				color: var(--color-black);
			  }
		}
		@media only screen and (max-width: 1130px) and (min-width: 769px)  {
			.colorContainer:last-child {
				height: 60px;
			}
			.colorName {
				display: -webkit-box;
    			-webkit-line-clamp: 2;
   				 -webkit-box-orient: vertical;
    			overflow: hidden;
    			text-overflow: ellipsis;
			}

		}
		@media (max-width: 768px) {

			.lampsSection {
				display: block;
				padding: 50px 0;
			}

			.lampsColors {
				padding: var(--large-padding) var(--small-padding);
			}

			.colorChoice {
				display: block;
				padding: 0px;
			}

			.description {
				max-width: 100%;
			}

			.colorList {
				padding: 0px;
				margin-left: 0px;
			}
			.colorList {
				column-gap: 35px;
				display: block;
			}
			.colorName {
				line-height: 39px;
				padding-left:5px;
			}
			.colorDot {
				margin-top: 8px;
			}
		 }

		`
	];

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.translations = state.productData.translations;
		this.decisions = state.decisions.data;
		this.profile = state.lightProfile.data.profile;
		this.lightProfile = state.lightProfile.data;
		this.productData = state.productData.productData;
		this.lang = state.status.lang;
		this.features = state.featureModel.data.features;

		// console.log('name of currently selected color: ', pimText(this.lang, 'HS_HousingColour', 'FV.S', this.decisions, this.translations));
		// this.updateSelectedColor(pimText(this.lang, 'HS_HousingColour', 'FV.S', this.decisions, this.translations));


		// the following only return values for data from the configuration engine backend
		// after selecting a luminare, do not work for our 'manual' configuration

		// is a feature already selected
		// console.log('is a housing color selected: ', isSatisfied('HS_HousingColour', this.decisions));

		// get the currently selected value id based on the users decisions
		// console.log('selected housing color: ', getValueOfFeature('HS_HousingColour', this.decisions));

		// get the translated label or other text for a feature
		// context
		// 'F': Label of the feature ('Color')
		// 'FV': Label of the selected value ('Blue')

		// 'FV.S': Short label of the selected value ('Blue')
		// 'FV.L': Long label of the selected value ('Azure Blue')
		// 'FV.D': Description of the selected value ('Blue is a nice color')
		// console.log('name of currently selected color: ', pimText(this.lang, 'HS_HousingColour', 'FV.S', this.decisions, this.translations));

		// Get other values from ProductData
		// console.log('hex color for a fixed value', this.getValueFromProductData('HS_HousingColour', 'AnthraciteGreyRAL7016')?.displayColour);



	}

	getValueFromProductData(featureId, featureValueId) {
		const pd = getProductData(featureId, this.productData);
		return pd?.values?.find(val => val.valueId === featureValueId);
	}

	render() {
		const s = this.state;

		// get all the colors
		// get color ids out of feature 'HS_HousingColour', get additional data from product data
		const colors = this.features?.find(
			feature => feature.variableId === 'HS_HousingColour'
		)?.values
			.map(value => this.getValueFromProductData('HS_HousingColour', value.featureValueId))
			.sort( (v1, v2) => (v1.displaySequenceUi > v2.displaySequenceUi ? 1 : -1)) ?? [];

		// currently selected color
		const selectedColor = getValueOfFeature('HS_HousingColour', this.decisions);


		return html`
			<section class="lampsColors">

				<div class="colorChoice">

					<div class="description">
						<h5>Mastfarben</h5>
						<p></p>
					</div>

					<div class="colorList">
						${repeat(colors, color => html`
							<div class="colorContainer ${classMap({selected: color.valueId === selectedColor})}">
								<p class="colorName">
									${pimText(this.lang, 'HS_HousingColour', 'FV.S', this.decisions, this.translations, color.valueId)}
									<span class="colorDot" style="background-color: ${color.displayColour}"></span>
								</p>
							</div>
						`)}
					</div>

			</section>

		`;
	}
}
